import React, { type ComponentType } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, RefreshCw, Database, Map, Play, MousePointerClick, Ear, ArrowLeftRight, Wrench, Sparkles, MonitorPlay, Mail, Smartphone, Users, Clock3, Globe2, Radio, Eye, Zap, ArrowLeft } from 'lucide-react';
const CLIENT_LOGOS = [{
  name: 'Roche',
  src: 'https://cdn.magicpatterns.com/uploads/oKM7AS6Say1ivXnufTeK4Q/Screenshot_2026-08-20_at_21.22.20.png'
}, {
  name: 'GSK',
  src: 'https://cdn.magicpatterns.com/uploads/chs5wo4p9K7rYZAXrMym5b/Screenshot_2026-08-20_at_21.22.24.png'
}, {
  name: 'Boehringer Ingelheim',
  src: 'https://cdn.magicpatterns.com/uploads/d5bawJHBsQSTkegSnvrVuj/Screenshot_2026-08-20_at_21.22.30.png'
}, {
  name: 'LEO Pharma',
  src: 'https://cdn.magicpatterns.com/uploads/pBTS69wAMQQDX9D3vkk2Rb/Screenshot_2026-08-20_at_21.22.35.png'
}, {
  name: 'Novartis',
  src: 'https://cdn.magicpatterns.com/uploads/uUutn2zPPVgFnTSiUdRWCm/Screenshot_2026-08-20_at_21.22.40.png'
}, {
  name: 'CSL',
  src: 'https://cdn.magicpatterns.com/uploads/pJnRcBrTsRx7HkvxQKP8x4/Screenshot_2026-08-20_at_21.23.27.png'
}, {
  name: 'Bayer',
  src: 'https://cdn.magicpatterns.com/uploads/gfWrjGPFiTnQ1dPmioRizg/Screenshot_2026-08-20_at_21.23.50.png'
}, {
  name: 'AbbVie',
  src: 'https://cdn.magicpatterns.com/uploads/68jEZZhKCb8PVjShWy7xh7/Screenshot_2026-08-20_at_21.24.11.png'
}];

// Reusable slide wrapper to enforce 16:9 safe areas, consistent padding, and navigation
function SlideWrapper({
  children,
  className = '',
  page,
  fullBleed = false,
  hideLogo = false
}: {
  children: React.ReactNode;
  className?: string;
  page?: number;
  fullBleed?: boolean;
  hideLogo?: boolean;
}) {
  const paddingClass = fullBleed ? 'p-0' : 'px-[6%] pb-[6%] pt-[12%]';
  return <div className={`relative flex h-full w-full flex-col overflow-hidden bg-[#FAFAFA] text-slate-900 ${paddingClass} ${className}`} data-id="element-4" data-mp="1iq2rld3d7ipr">
      {!hideLogo && <div className="absolute left-[6%] top-[5%] z-50" data-id="element-5" data-mp="3d32bvjiox13d">
          <img src="https://cdn.magicpatterns.com/uploads/cYecvHgbFGxT9gjSDQWm9C/snakker_logo_new2.png" alt="Snakker Logo" className="w-[120px] object-contain" data-id="element-6" data-mp="3d2j2ir73vl0y" />
        </div>}
      {children}
      {page && <div className="absolute bottom-6 right-8 z-50 flex items-center gap-4 text-[clamp(12px,1vw,16px)] font-medium text-slate-400" data-id="element-7" data-mp="ky4ql714m1ry">
          <button onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'ArrowLeft'
      }))} className="hover:text-[var(--brand)] transition-colors" aria-label="Previous slide" data-id="element-8" data-mp="bvvj6q15mois">
            <ArrowLeft className="h-5 w-5" data-id="element-9" data-mp="14kkjimudypth" />
          </button>
          <span data-id="element-10" data-mp="zs0xr2ljoodl">{page} / 11</span>
          <button onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'ArrowRight'
      }))} className="hover:text-[var(--brand)] transition-colors" aria-label="Next slide" data-id="element-11" data-mp="zr81ivk7ph9p">
            <ArrowRight className="h-5 w-5" data-id="element-12" data-mp="1niwvshglgfta" />
          </button>
        </div>}
    </div>;
}

// Reusable staggered fade-in container
function StaggerContainer({
  children,
  className = '',
  delay = 0.2
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  return <motion.div initial="hidden" animate="visible" variants={{
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: delay
      }
    }
  }} className={className} data-id="element-13" data-mp="24rz6mivj3d8v">
      {children}
    </motion.div>;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};

// --- SLIDES ---

function Slide0_MeetSnakker() {
  return <SlideWrapper page={1} data-id="element-14" data-mp="2si1usnwbnz27">
      <div className="flex min-h-0 flex-1 items-center justify-between gap-8" data-id="element-15" data-mp="2sg8m7325f4rk">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6
      }} className="flex w-[50%] flex-col justify-center" data-id="element-16" data-mp="2wjwu5mup02dc">
          <h2 className="text-[clamp(40px,5vw,80px)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-17" data-mp="2nug6kd3ziktu">
            HCPs don’t lack content.<br data-id="element-18" data-mp="2jrtj0oecjm2g" />
            <span className="text-[var(--brand)]" data-id="element-19" data-mp="2xlb2yaay30t0">They lack time.</span>
          </h2>

          <p className="mt-8 text-[clamp(20px,2.5vw,36px)] font-medium leading-snug text-[var(--muted)]" data-id="element-20" data-mp="2xn5eiy0btgkf">
            Snakker creates Snaks™.<br data-id="element-21" data-mp="24w3tig9ycqsz" />
            Short, sharp, and satisfying stories engineered for attention.
          </p>

          <div className="mt-12" data-id="element-22" data-mp="20vtph95q0agr">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--brand)]" data-id="element-23" data-mp="20wmofnyftvoc">Trusted by</p>
            <div className="mt-4 flex flex-wrap gap-4" data-id="element-24" data-mp="2ew4j2jyf035m">
              {CLIENT_LOGOS.slice(0, 5).map(client => <img key={client.name} src={client.src} alt={client.name} className="h-8 object-contain opacity-60 grayscale" data-id="element-25" data-mp="29y2zzz0zo3u5" />)}
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex h-full min-h-0 w-[45%] items-center justify-end" data-id="element-26" data-mp="3ld9hd35oxcad">
          <img src="https://cdn.magicpatterns.com/uploads/6mnx8ZCX32yC1VvG3s2hca/image.png" alt="Hemlibra Snak Example" className="h-full w-full object-contain drop-shadow-2xl" data-id="element-27" data-mp="y3hhlodm9pmi" />
        </motion.div>
      </div>
    </SlideWrapper>;
}
function Slide0b_WhatIsASnakFormat() {
  return <SlideWrapper className="flex flex-col" page={2} data-id="element-28" data-mp="1urx57ex61owz">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="flex-shrink-0 text-center" data-id="element-29" data-mp="1h23j34d1piqy">
        <h2 className="text-[clamp(40px,5vw,80px)] font-bold tracking-tight text-[var(--fg)]" data-id="element-30" data-mp="29pfx2q554i8n">
          What is a <span className="text-[var(--brand)]" data-id="element-31" data-mp="2egxskyoe9qm8">Snak?</span>
        </h2>
        <p className="mt-4 text-[clamp(20px,2.5vw,36px)] font-medium text-[var(--muted)]" data-id="element-32" data-mp="2ococy49e9bqy">
          A format to explain complex issues in a short, satisfying way.
        </p>
      </motion.header>

      <StaggerContainer className="mt-10 grid min-h-0 flex-1 grid-cols-3 gap-8 pb-4" data-id="element-33" data-mp="2eeiclr38kdo7">
        <motion.div variants={fadeUp} className="flex min-h-0 flex-col items-center text-center" data-id="element-34" data-mp="2egbiv59xsvvo">
          <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-2xl bg-slate-100 shadow-inner" data-id="element-35" data-mp="29hrs8rkeevwz">
            <img src="https://cdn.magicpatterns.com/uploads/9PcYYi1bjYDtkFKLHb4HXN/Screenshot_2026-08-24_at_10.20.50.png" alt="Highly visual" className="h-full w-full object-cover" data-id="element-36" data-mp="20mtwlcpv951y" />
          </div>
          <h3 className="mt-6 flex-shrink-0 text-[clamp(20px,2.5vw,32px)] font-bold text-[var(--fg)]" data-id="element-37" data-mp="1qppb7f3uhdm7">Highly visual</h3>
          <p className="mt-2 flex-shrink-0 text-[clamp(14px,1.5vw,20px)] text-[var(--muted)]" data-id="element-38" data-mp="1lojhbdyg7kb3">Understood at a glance.</p>
        </motion.div>

        <motion.div variants={fadeUp} className="flex min-h-0 flex-col items-center text-center" data-id="element-39" data-mp="1hqlswdci528r">
          <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-2xl bg-slate-100 shadow-inner" data-id="element-40" data-mp="3tdxp7j8wv6ct">
            <img src="https://cdn.magicpatterns.com/uploads/2H17bwrMVXk3HzB54QFkf7/Screenshot_2026-08-24_at_10.19.16.png" alt="Adaptive" className="h-full w-full object-cover object-left" data-id="element-41" data-mp="66lth8tjo4dg" />
          </div>
          <h3 className="mt-6 flex-shrink-0 text-[clamp(20px,2.5vw,32px)] font-bold text-[var(--fg)]" data-id="element-42" data-mp="39on7y0tiodcl">Adaptive</h3>
          <p className="mt-2 flex-shrink-0 text-[clamp(14px,1.5vw,20px)] text-[var(--muted)]" data-id="element-43" data-mp="3engj3mlbzs9l">Reads signals and adjusts.</p>
        </motion.div>

        <motion.div variants={fadeUp} className="flex min-h-0 flex-col items-center text-center" data-id="element-44" data-mp="3oci1fjfcmlqq">
          <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-2xl bg-slate-100 shadow-inner" data-id="element-45" data-mp="2qx43rqnrat8z">
            <img src="https://cdn.magicpatterns.com/uploads/w1FNxYqhnWeramphNUuzps/Screenshot_2026-08-24_at_10.16.43.png" alt="Satisfying" className="h-full w-full object-cover object-top" data-id="element-46" data-mp="2vv5osh4qt2n3" />
          </div>
          <h3 className="mt-6 flex-shrink-0 text-[clamp(20px,2.5vw,32px)] font-bold text-[var(--fg)]" data-id="element-47" data-mp="1kb3f8ki98mp6">Satisfying</h3>
          <p className="mt-2 flex-shrink-0 text-[clamp(14px,1.5vw,20px)] text-[var(--muted)]" data-id="element-48" data-mp="1yb4clpzb9bzw">Rewards the attention invested.</p>
        </motion.div>
      </StaggerContainer>
    </SlideWrapper>;
}
function Slide1_Problem() {
  return <SlideWrapper page={3} data-id="element-49" data-mp="238nssvfkjyuh">
      <div className="flex min-h-0 flex-1 items-center justify-between gap-8" data-id="element-50" data-mp="239fos7d2vyla">
        {/* Left Column: Text & Doorway */}
        <div className="flex h-full min-h-0 w-[30%] flex-col justify-between py-4" data-id="element-51" data-mp="2gzj6qm4xd64x">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex-shrink-0 text-[clamp(28px,3.5vw,56px)] font-bold leading-[1.1] tracking-tight" data-id="element-52" data-mp="2czijcbzulklq">
            Pharma has plenty of science.<br data-id="element-53" data-mp="35ke6l15c4he2" />
            <span className="text-[var(--muted)]" data-id="element-54" data-mp="35lz14qrng0bx">The challenge is getting people into it.</span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.8,
          duration: 0.6
        }} className="my-auto flex aspect-square w-[60%] max-w-[180px] flex-shrink-0 flex-col items-center justify-center rounded-2xl bg-[var(--brand)] shadow-2xl" data-id="element-55" data-mp="3ajidfl4se318">
            <span className="text-[clamp(14px,1.8vw,24px)] font-bold tracking-widest text-white" data-id="element-56" data-mp="2mscxz72rdcrg">ATTENTION</span>
            <ArrowRight className="mt-2 h-6 w-6 text-white/50" data-id="element-57" data-mp="2cw9wm3fqdi5k" />
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.2
        }} className="flex-shrink-0 text-[clamp(18px,2vw,28px)] font-medium leading-snug text-[var(--brand)]" data-id="element-58" data-mp="2cu5m32nbtkwb">
            You have to earn the right to get to the scientific conversation.
          </motion.p>
        </div>

        {/* Right Column: Massive Video */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.4,
        duration: 0.8
      }} className="relative flex h-full min-h-0 w-[70%] items-center justify-center overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200" data-id="element-59" data-mp="teyqe4tvuo4x">
          <video src="https://cdn.magicpatterns.com/uploads/57ZMHM6UpgVeWweLj1VZwf/affinity.mp4" autoPlay loop muted playsInline className="h-full w-full object-contain" data-id="element-60" data-mp="ofm1zfnd5908" />
        </motion.div>
      </div>
    </SlideWrapper>;
}
function Slide3_Episodic() {
  return <SlideWrapper page={4} className="flex flex-col" data-id="element-61" data-mp="17bc73bqlelic">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex-shrink-0 text-[clamp(40px,5vw,80px)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-62" data-mp="1v2965nxahoc6">
        Don’t compress the 'movie'.<br data-id="element-63" data-mp="3ki22jj3u4fro" />
        <span className="text-[var(--brand)]" data-id="element-64" data-mp="3khsc5uevo6lg">Turn it into a series.</span>
      </motion.h2>

      <div className="mt-8 flex min-h-0 flex-1 items-center justify-between gap-8" data-id="element-65" data-mp="3fjqtvbpa0bb1">
        {/* Left: Monolith */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.3
      }} className="relative flex h-full min-h-0 w-[25%] flex-col items-center justify-center overflow-hidden rounded-xl bg-slate-200 shadow-inner ring-1 ring-slate-200" data-id="element-66" data-mp="3u8eo9endyeze">
          <img src="https://cdn.magicpatterns.com/uploads/2B25zz3xzXz3iLPoG2wQgS/Screenshot_2026-08-24_at_11.36.13.png" alt="Dense Content Collection" className="h-full w-full object-cover opacity-90" data-id="element-67" data-mp="2wnima18sjock" />
          <div className="absolute inset-0 bg-black/10" data-id="element-68" data-mp="qroagdlkaxn5"></div>
        </motion.div>

        <ArrowRight className="h-10 w-10 flex-shrink-0 text-slate-300" data-id="element-69" data-mp="ltw80pnajt4p" />

        {/* Right: Episodes */}
        <div className="flex h-full min-h-0 w-[65%] flex-col justify-between gap-4" data-id="element-70" data-mp="sx2tjn9qypha">
          <StaggerContainer className="flex min-h-0 flex-1 gap-4" delay={0.6} data-id="element-71" data-mp="xve27uob2txx">
            {["https://cdn.magicpatterns.com/uploads/f9VXuGD5iawbzpT2bm6J7d/Screenshot_2026-08-24_at_11.26.03.png", "https://cdn.magicpatterns.com/uploads/8VHtVncWAcRapn6m1kL1Bf/Screenshot_2026-08-24_at_11.26.17.png", "https://cdn.magicpatterns.com/uploads/43QqFtN3wAVVwSnDtbqEPa/Screenshot_2026-08-24_at_11.26.30.png", "https://cdn.magicpatterns.com/uploads/6eJdzXtDSKTjtFwn4nfj6E/Screenshot_2026-08-24_at_11.26.23.png"].map((src, i) => <motion.div key={i} variants={fadeUp} className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl shadow-xl" data-id="element-72" data-mp="ouzg4t3ipatd" data-mpi={i}>
                <img src={src} alt={`Episode ${i + 1}`} className="h-full w-full object-cover" data-id="element-73" data-mp="3nk6kjnpccdwh" />
              </motion.div>)}
          </StaggerContainer>
          
          {/* Suspense Curve */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.2
        }} className="relative mt-2 h-[12%] min-h-[40px] w-full flex-shrink-0" data-id="element-74" data-mp="29oenq91w5d4j">
            <svg viewBox="0 0 400 50" className="h-full w-full overflow-visible fill-none stroke-[var(--brand)] stroke-[3px]" data-id="element-75" data-mp="20moxh6jqwk8c">
              <path d="M 0,50 Q 25,0 50,25 T 100,50 Q 125,0 150,25 T 200,50 Q 225,0 250,25 T 300,50 Q 325,0 350,25 T 400,50" data-id="element-76" data-mp="1vrjas5iir1j7" />
            </svg>
            <div className="absolute inset-0 flex justify-between px-[12.5%] text-[clamp(12px,1vw,16px)] font-medium text-slate-400" data-id="element-77" data-mp="1lyaxd3dpr9vh">
              <span className="mt-auto" data-id="element-78" data-mp="1hy0fascnct09">Hook</span><span className="mt-auto" data-id="element-79" data-mp="1cyxn48hrmh9p">Hook</span><span className="mt-auto" data-id="element-80" data-mp="1czyx09n1oxpu">Hook</span><span className="mt-auto" data-id="element-81" data-mp="1cwkwz81ut0lk">Resolve</span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="mt-8 flex flex-shrink-0 items-end justify-between" data-id="element-82" data-mp="130hrbs7smjtv">
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5
      }} className="max-w-[50%] text-[clamp(18px,2vw,32px)] font-medium text-slate-700" data-id="element-83" data-mp="132kt19ib61ef">
          Each episode resolves one idea while creating a reason to continue.
        </motion.p>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.7
      }} className="max-w-[40%] text-right text-[clamp(14px,1.5vw,24px)] text-slate-500" data-id="element-84" data-mp="2tc68ya5nee2c">
          The story progresses step by step from what the HCP understands today to where the brand needs to take them.
        </motion.p>
      </div>
    </SlideWrapper>;
}
function Slide4_Anatomy() {
  return <SlideWrapper className="items-center justify-center bg-white" page={5} fullBleed hideLogo data-id="element-85" data-mp="2af4yi9tbrlr0">
      <motion.img initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8
    }} src="https://cdn.magicpatterns.com/uploads/imAonTm4u7sTnwCkc3v2Z4/Screenshot_2026-08-24_at_11.28.54.png" alt="The Anatomy of a Snak" className="h-full w-full object-contain" data-id="element-86" data-mp="2fe7uz5v8ojnd" />
    </SlideWrapper>;
}
function Slide5_Satisfying() {
  return <SlideWrapper className="flex flex-col justify-center" page={6} data-id="element-87" data-mp="1t5ixk4el6tmb">
      <div className="flex min-h-0 flex-1 items-center justify-between gap-12" data-id="element-88" data-mp="27tni7dx79f7q">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6
      }} className="flex w-[45%] flex-col justify-center" data-id="element-89" data-mp="27vqhkok8skp4">
          <h2 className="flex-shrink-0 text-[clamp(40px,5vw,80px)] font-bold tracking-tight text-[var(--fg)]" data-id="element-90" data-mp="2gw6h5mxv77op">
            The reward is not a prize.
          </h2>
          <p className="mt-8 flex-shrink-0 text-[clamp(24px,3vw,48px)] font-medium leading-snug text-[var(--muted)]" data-id="element-91" data-mp="2qrzu37fhx370">
            Interaction is part of the story.<br data-id="element-92" data-mp="we8pwnfxrbiy" />
            <span className="font-bold text-[var(--brand)]" data-id="element-93" data-mp="wdp6f2an0f2t">Not decoration.</span>
          </p>
          <div className="mt-12 flex flex-col gap-6 text-[clamp(18px,2vw,28px)] font-medium text-[var(--muted)]" data-id="element-94" data-mp="rg74vwyy924u">
            <p data-id="element-95" data-mp="2rioq782d01wv">✓ "Was I right?" (Prediction)</p>
            <p data-id="element-96" data-mp="2rjzt75nzd5mx">✓ "I didn't know that." (Surprise)</p>
            <p data-id="element-97" data-mp="2whj8m8wezogg">✓ "What do others think?" (Peer insight)</p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex h-full min-h-0 w-[55%] items-center justify-center" data-id="element-98" data-mp="30haap8re5r0a">
          <img src="https://cdn.magicpatterns.com/uploads/w1FNxYqhnWeramphNUuzps/Screenshot_2026-08-24_at_10.16.43.png" alt="Peer Comparison UI" className="h-full w-full object-contain drop-shadow-2xl" data-id="element-99" data-mp="3ffm518raknoa" />
        </motion.div>
      </div>
    </SlideWrapper>;
}
function Slide6_ListensBack() {
  return <SlideWrapper page={7} className="flex flex-col" data-id="element-100" data-mp="3t8bp37n9fiir">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex-shrink-0 text-[clamp(36px,4.5vw,72px)] font-bold leading-[1.1] tracking-tight" data-id="element-101" data-mp="20g1a826g344">
        Traditional content talks.<br data-id="element-102" data-mp="74i6rcql1y6q" />
        <span className="text-[var(--brand)]" data-id="element-103" data-mp="74rzh830ijg4">A Snak listens back.</span>
      </motion.h2>

      <div className="mt-6 flex min-h-0 w-full flex-1 items-center justify-center rounded-2xl bg-white p-2 shadow-inner ring-1 ring-slate-200" data-id="element-104" data-mp="27hz4xe34ndi">
        <motion.img initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.8
      }} src="https://cdn.magicpatterns.com/uploads/7vd1ViVemKP8aiuT1hfYYr/Screenshot_2026-08-24_at_10.19.50.png" alt="Branching Logic Data" className="h-full w-full object-contain" data-id="element-105" data-mp="3uclvcq85wjso" />
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.8
    }} className="mt-6 flex flex-shrink-0 items-center justify-between" data-id="element-106" data-mp="h37nh26qtcle">
        <p className="text-[clamp(20px,2.5vw,36px)] font-medium text-[var(--fg)]" data-id="element-107" data-mp="18tc4cqvanhhv">
          The next story is informed by the last one.
        </p>
        <div className="flex items-center gap-4 text-[clamp(14px,1.8vw,20px)] font-bold tracking-widest text-[var(--brand)]" data-id="element-108" data-mp="1x9e3hh23m9ez">
          <span data-id="element-109" data-mp="1ilsxnfdu9w3d">TELL</span> <ArrowRight className="h-5 w-5" data-id="element-110" data-mp="1imb5785ybwql" />
          <span data-id="element-111" data-mp="1njl196nuj6ne">LISTEN</span> <ArrowRight className="h-5 w-5" data-id="element-112" data-mp="1nj1i5mmgmc7s" />
          <span data-id="element-113" data-mp="1nk46pnwb82kj">ADAPT</span>
        </div>
      </motion.div>
    </SlideWrapper>;
}
function Slide7_WhySnakker() {
  return <SlideWrapper page={8} className="flex flex-col justify-between" data-id="element-114" data-mp="2g8i6a831r6y7">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex-shrink-0 text-[clamp(36px,4.5vw,72px)] font-bold leading-[1.1] tracking-tight" data-id="element-115" data-mp="2g7ql7r72srm6">
        The tool was never the difficult part.<br data-id="element-116" data-mp="pwc7kodoyrab" />
        <span className="text-[var(--brand)]" data-id="element-117" data-mp="pvkm46dt69xr">The story was.</span>
      </motion.h2>

      <div className="mt-6 flex min-h-0 w-full flex-1 gap-8" data-id="element-118" data-mp="uuxch13w1z53">
        <StaggerContainer className="flex min-h-0 w-[35%] flex-col gap-4" data-id="element-119" data-mp="uwgougmfbq1l">
          <motion.div variants={fadeUp} className="flex flex-shrink-0 flex-col items-center justify-center rounded-2xl bg-slate-100 p-4 text-center" data-id="element-120" data-mp="391n0t5of1x5c">
            <Wrench className="mb-2 h-8 w-8 text-slate-400" data-id="element-121" data-mp="3i3moggtxrzh9" />
            <span className="text-[clamp(14px,1.8vw,24px)] font-bold tracking-widest text-slate-400" data-id="element-122" data-mp="3i2tnjwhns471">TECHNOLOGY</span>
          </motion.div>
          <motion.div variants={fadeUp} className="relative flex min-h-0 flex-1 overflow-hidden rounded-2xl bg-[#1a1512] shadow-xl ring-1 ring-slate-200" data-id="element-123" data-mp="2qc70ojgn86pg">
            <img src="https://cdn.magicpatterns.com/uploads/3qgikYAL5ZQnnZa5vdot2Q/Screenshot_2026-08-24_at_11.58.03.png" alt="Storytelling" className="h-full w-full object-contain" data-id="element-124" data-mp="2lex0q9vmocnd" />
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40" data-id="element-125" data-mp="26o7px8m2wzk1">
              <span className="text-[clamp(14px,1.8vw,24px)] font-bold tracking-widest text-white drop-shadow-md" data-id="element-126" data-mp="22n48ihoduvgp">STORY</span>
            </div>
          </motion.div>
        </StaggerContainer>

        <StaggerContainer className="flex min-h-0 w-[65%] flex-col justify-center gap-4 py-2" delay={0.6} data-id="element-127" data-mp="sn3e1hvc8gdp">
          {[{
          role: 'ABBVIE',
          desc: 'Owns the science, evidence, objective and market knowledge'
        }, {
          role: 'SNAKKER',
          desc: 'Finds the story, constructs the narrative and produces the experience'
        }, {
          role: 'AI',
          desc: 'Makes the craftsmanship scalable across brands, campaigns and countries'
        }].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex items-center gap-6 border-b border-slate-200 pb-4 last:border-0 last:pb-0" data-id="element-128" data-mp="1bfp41ljkdcqd" data-mpi={i}>
              <span className="w-[100px] flex-shrink-0 text-[clamp(14px,1.8vw,20px)] font-black tracking-widest text-slate-900" data-id="element-129" data-mp="1kdrdmphd5dp9">{item.role}</span>
              <span className="text-[clamp(14px,1.8vw,24px)] text-slate-600" data-id="element-130" data-mp="3r4z3l1gfdgyg">{item.desc}</span>
            </motion.div>)}
        </StaggerContainer>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5
    }} className="mt-6 flex-shrink-0 text-center" data-id="element-131" data-mp="21omtk1r9inaq">
        <p className="text-[clamp(18px,2vw,32px)] font-medium leading-tight text-slate-800" data-id="element-132" data-mp="1svjig043amj9">
          We don’t turn scientific brand managers into storytellers.<br data-id="element-133" data-mp="1wvbtcjgyh4rs" />
          <span className="font-bold text-[var(--brand)]" data-id="element-134" data-mp="1wusbf2rb2gdr">We give them a storytelling capability.</span>
        </p>
      </motion.div>
    </SlideWrapper>;
}
function Slide8_Website() {
  return <SlideWrapper page={9} className="flex flex-col" data-id="element-135" data-mp="1nxhx18j6yb4m">
      <div className="flex min-h-0 flex-1 items-center justify-between gap-12" data-id="element-136" data-mp="19x79ofh3beo7">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6
      }} className="flex h-full min-h-0 w-[45%] flex-col justify-center" data-id="element-137" data-mp="1dxr255qpxu40">
          <h2 className="flex-shrink-0 text-[clamp(36px,4.5vw,72px)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-138" data-mp="38b0i9gdrsdv8">
            Reverse the traditional<br data-id="element-139" data-mp="l188d8rzfatn" />
            <span className="text-[var(--brand)]" data-id="element-140" data-mp="g4rkmfop2yh3">website experience.</span>
          </h2>
          
          <div className="mt-8 flex min-h-0 flex-col gap-4 overflow-y-auto text-[clamp(16px,1.8vw,24px)] font-medium leading-relaxed text-[var(--muted)]" data-id="element-141" data-mp="c2ecjkmh6oh5">
            <p data-id="element-142" data-mp="75nwgxao7svq">
              <strong className="text-[var(--fg)]" data-id="element-143" data-mp="76640q2s9tiy">Start lean-back.</strong> Begin with an animated introduction that requires almost no effort, but quickly delivers value.
            </p>
            <p data-id="element-144" data-mp="zulfj2mphtpj">
              <strong className="text-[var(--fg)]" data-id="element-145" data-mp="v1saser8mldg">Gradually introduce depth.</strong> As users stay and engage, move from a simple, guided flow into a more personalized experience.
            </p>
            <p data-id="element-146" data-mp="q0urfyuhrlad">
              <strong className="text-[var(--fg)]" data-id="element-147" data-mp="1nhtaorqf7mdu">Timing matters.</strong> Increase effort and complexity only when the user is ready for it.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.8
      }} className="flex h-full min-h-0 w-[55%] items-center justify-center" data-id="element-148" data-mp="1q2r8a4p2n0i1">
          <img src="https://cdn.magicpatterns.com/uploads/juyDCwtopMRwntfuXQvPax/Screenshot_2026-08-24_at_11.04.26.png" alt="User Readiness vs Interaction Complexity Graph" className="h-full w-full object-contain drop-shadow-xl" data-id="element-149" data-mp="1zz42r4sfxidu" />
        </motion.div>
      </div>
    </SlideWrapper>;
}
function Slide9_Congress() {
  return <SlideWrapper page={10} className="items-center justify-center bg-[#F9F9F9]" fullBleed hideLogo data-id="element-150" data-mp="2is7wsr9klx8z">
      <motion.img initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8
    }} src="https://cdn.magicpatterns.com/uploads/nDTcCpxqMi3fWVtFzEfjZs/Screenshot_2026-08-24_at_13.05.53.png" alt="The Congress Journey" className="h-full w-full object-cover" data-id="element-151" data-mp="2np84f6m2j827" />
    </SlideWrapper>;
}
function Slide10_Closeout() {
  return <SlideWrapper page={11} className="items-center justify-center text-center" data-id="element-152" data-mp="2f35i11saftok">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="flex max-w-[80%] flex-col items-center" data-id="element-153" data-mp="2su0qicyvk8rm">
        <h2 className="text-[clamp(48px,6vw,96px)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-154" data-mp="2nxa5rcc4kozv">
          Ready to rethink your <span className="text-[var(--brand)]" data-id="element-155" data-mp="1w4jezhd7nsi0">engagement?</span>
        </h2>
        <p className="mt-8 text-[clamp(20px,2.5vw,36px)] font-medium leading-relaxed text-[var(--muted)]" data-id="element-156" data-mp="2a39cgy3xzu6w">
          Discover how Snakker’s short, episodic storytelling can support your commercial and medical engagement strategies.
        </p>
        <button className="mt-12 rounded-full bg-[var(--brand)] px-10 py-5 text-[clamp(18px,2vw,28px)] font-bold text-white shadow-xl transition-transform hover:scale-105" data-id="element-157" data-mp="3lhehlufw6a3t">
          Get in touch
        </button>
      </motion.div>
    </SlideWrapper>;
}
export const slides: DeckSlide[] = [{
  id: 'scr_odohm2',
  title: 'Meet Snakker',
  component: Slide0_MeetSnakker
}, {
  id: 'scr_blr9pa',
  title: 'What is a Snak?',
  component: Slide0b_WhatIsASnakFormat
}, {
  id: 'scr_problem',
  title: 'The Problem',
  component: Slide1_Problem
}, {
  id: 'scr_episodic',
  title: 'Episodic Storytelling',
  component: Slide3_Episodic
}, {
  id: 'scr_anatomy',
  title: 'Anatomy of a Snak',
  component: Slide4_Anatomy
}, {
  id: 'scr_satisfying',
  title: 'What makes it satisfying?',
  component: Slide5_Satisfying
}, {
  id: 'scr_listens',
  title: 'The Story Listens Back',
  component: Slide6_ListensBack
}, {
  id: 'scr_whysnakker',
  title: 'Why Snakker?',
  component: Slide7_WhySnakker
}, {
  id: 'scr_website',
  title: 'Snakkerfication of Websites',
  component: Slide8_Website
}, {
  id: 'scr_r91cdb',
  title: 'The Congress Journey',
  component: Slide9_Congress
}, {
  id: 'scr_closeout',
  title: 'Get in Touch',
  component: Slide10_Closeout
}];